/**
 * Phase 36 Batch AJ — Score Breakdown Panel.
 *
 * Renders directly under the Tri-View Deal Score on the Summary tab.
 * Answers two questions the user asked verbatim:
 *
 *   1. "Why 72/100?"     → per-category contribution table
 *   2. "How to move to Buy?" → 2-3 "move the needle" levers from the
 *                              GET /api/deals/{id}/score-breakdown
 *                              endpoint
 *
 * Re-fetches whenever the deal's effective price changes so the
 * breakdown stays in sync with the tri-view's active basis (e.g. the
 * user clicked Counter-offer — the breakdown re-anchors on the new
 * effective price).
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  TrendingUp,
  Banknote,
  Wallet,
  Building2,
  ShieldAlert,
  MapPin,
  Loader2,
} from "lucide-react";


const CATEGORY_ICON = {
  cash_flow: Banknote,
  cap_rate: TrendingUp,
  cash_on_cash: Wallet,
  dscr: Building2,
  risk: ShieldAlert,
  market: MapPin,
  // Phase 47.1 — BRRRR-specific component keys (returned by the
  // backend's BRRRR branch). Fall back to a sensible icon for each.
  capital_recycled: Wallet,
  equity_created: TrendingUp,
  post_refi_cash_flow: Banknote,
  refi_dscr: Building2,
  data_confidence: ShieldAlert,
};

const STATUS_COPY = {
  strong: {
    label: "Strong",
    icon: CheckCircle2,
    bar: "bg-emerald-500",
    chip: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
  },
  below_target: {
    label: "Below target",
    icon: AlertTriangle,
    bar: "bg-amber-500",
    chip: "bg-amber-100 text-amber-800 ring-1 ring-amber-200",
  },
  weak: {
    label: "Weak",
    icon: AlertTriangle,
    bar: "bg-rose-500",
    chip: "bg-rose-100 text-rose-800 ring-1 ring-rose-200",
  },
};


function fmtMoney(v) {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  return "$" + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}


export default function ScoreBreakdownPanel({ deal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const fetchBreakdown = useCallback(async () => {
    if (!deal?.id) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const { data: payload } = await api.get(
        `/deals/${deal.id}/score-breakdown`
      );
      setData(payload);
      try {
        window.posthog?.capture?.("score_breakdown_viewed", {
          deal_id: deal.id,
          score: payload?.total_score,
          verdict: payload?.verdict,
        });
      } catch { /* posthog blocked */ }
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e?.message || "Could not load breakdown.");
    } finally {
      setLoading(false);
    }
    // Re-fetch when the effective price / basis / score mutate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deal?.id,
    deal?.purchase_price,
    deal?.effective_purchase_price_basis,
    deal?.deal_score,
  ]);

  useEffect(() => {
    fetchBreakdown();
  }, [fetchBreakdown]);

  if (loading && !data) {
    return (
      <section
        data-testid="score-breakdown-loading"
        className="h-44 animate-pulse rounded-2xl bg-slate-100"
      />
    );
  }

  if (errorMsg) {
    return (
      <section
        data-testid="score-breakdown-error"
        className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          <p className="font-medium">Could not load score breakdown</p>
          <p className="mt-0.5 text-xs">{errorMsg}</p>
        </div>
      </section>
    );
  }
  if (!data) return null;

  const { total_score, verdict, categories, levers, buy_threshold } = data;
  const pointsToBuy = Math.max(0, (buy_threshold || 75) - (total_score || 0));

  return (
    <section
      data-testid="score-breakdown"
      className="rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <Sparkles className="h-3 w-3" /> Score breakdown
          </div>
          <h3 className="mt-1 text-lg font-bold text-slate-900">
            Why {total_score}/100?
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Verdict: <span className="font-semibold text-slate-700">{verdict}</span>
            {pointsToBuy > 0 && (
              <> · <span className="font-medium text-indigo-700">
                {pointsToBuy} point{pointsToBuy === 1 ? "" : "s"} to Buy
              </span></>
            )}
          </p>
        </div>
        <button
          type="button"
          data-testid="score-breakdown-toggle"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center justify-center rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          {open ? (
            <>
              Hide <ChevronUp className="ml-1 h-3 w-3" />
            </>
          ) : (
            <>
              Show <ChevronDown className="ml-1 h-3 w-3" />
            </>
          )}
        </button>
      </header>

      {open && (
        <div className="grid gap-4 p-5 md:grid-cols-3">
          {/* Categories table (2 cols) */}
          <div className="md:col-span-2">
            <table className="w-full text-sm" data-testid="score-breakdown-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="pb-2 text-left font-semibold">Category</th>
                  <th className="pb-2 text-left font-semibold">Value</th>
                  <th className="pb-2 text-right font-semibold">Score</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => {
                  const Icon = CATEGORY_ICON[c.key] || Sparkles;
                  const status = STATUS_COPY[c.status] || STATUS_COPY.below_target;
                  const StatusIcon = status.icon;
                  const pct = c.max > 0 ? (c.score / c.max) * 100 : 0;
                  return (
                    <tr
                      key={c.key}
                      data-testid={`score-breakdown-row-${c.key}`}
                      className="border-t border-slate-100"
                    >
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 flex-none text-slate-500" />
                          <span className="font-medium text-slate-800">{c.label}</span>
                        </div>
                        <p className="ml-6 mt-0.5 text-[11px] text-slate-500">
                          {c.detail}
                        </p>
                      </td>
                      <td className="py-3 tabular-nums text-slate-700">
                        {c.value}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="flex w-24 items-center gap-1">
                            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                              <div
                                className={`h-full rounded-full ${status.bar} transition-all`}
                                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                              />
                            </div>
                          </div>
                          <span className="w-12 text-right text-xs font-semibold tabular-nums text-slate-700">
                            {c.score}/{c.max}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${status.chip}`}
                          >
                            <StatusIcon className="h-3 w-3" />
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-slate-200 font-semibold">
                  <td className="pt-3 text-slate-900">Total</td>
                  <td className="pt-3 text-slate-700">{verdict}</td>
                  <td className="pt-3 text-right text-slate-900">
                    <span data-testid="score-breakdown-total">
                      {total_score}/100
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Levers (1 col) */}
          <aside className="md:col-span-1">
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
              <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
                <TrendingUp className="h-3 w-3" /> Move to Buy ({buy_threshold || 75}+)
              </div>
              {levers && levers.length > 0 ? (
                <ul
                  data-testid="score-breakdown-levers"
                  className="mt-3 space-y-2"
                >
                  {levers.map((lev, idx) => (
                    <li
                      key={lev.type}
                      data-testid={`score-lever-${lev.type}`}
                      className="rounded-lg border border-white bg-white p-3 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            {lev.label}
                          </p>
                          {lev.delta_label && (
                            <p className="mt-0.5 text-[11px] tabular-nums text-slate-500">
                              {lev.delta_label}
                            </p>
                          )}
                        </div>
                        <span className="inline-flex flex-none items-center gap-0.5 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 ring-1 ring-emerald-200">
                          +{Math.max(0, (lev.expected_score || 0) - (total_score || 0))} pts
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-slate-500">
                        Hits {lev.expected_score}/100 ({lev.expected_verdict})
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-xs text-slate-600">
                  {(total_score || 0) >= (buy_threshold || 75)
                    ? "You're already at Buy. No lever needed."
                    : "No single lever moves this to Buy. Multiple inputs need to improve."}
                </p>
              )}
              {(loading) && (
                <Loader2 className="mt-2 h-3 w-3 animate-spin text-indigo-500" />
              )}
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
